import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllSmartQrCode,
  getSingleSmartQrCode,
  createSmartQrCode,
  updateSmartQrCode,
  deleteSmartQrCode,
  toggleSmartQrCode,
} from "@/api/online-ordering/settings/smartQrCode";

// Thunks

export const fetchAllSmartQrCode = createAsyncThunk(
  "smartQrCodes/fetchAll",
  async (arg, { rejectWithValue }) => {
    let restaurant_id;
    let page = 1;
    let per_page = 10;
    let search_term = "";
    let status = "";
    if (typeof arg === "object" && arg !== null) {
      restaurant_id = arg.restaurant_id;
      page = arg.page || 1;
      per_page = arg.per_page || 10;
      search_term = arg.search_term || "";
      status = arg.status || "";
    } else {
      restaurant_id = arg;
    }
    const res = await getAllSmartQrCode(restaurant_id, page, per_page, search_term, status);
    console.log("fetchAllSmartQrCode response:", res);
    if (res?.status === "success" || res?.success) {
      return {
        smartQrCodes: res.data?.smart_qr_codes || res.data || res.smart_qr_codes || [],
        pagination: res.data?.pagination || null
      };
    }
    // if it returns an array directly
    if (Array.isArray(res)) return { smartQrCodes: res, pagination: null };
    if (Array.isArray(res?.data)) return { smartQrCodes: res.data, pagination: null };
    
    return rejectWithValue(res?.message || "Failed to fetch smart QR codes");
  }
);

export const fetchSingleSmartQrCode = createAsyncThunk(
  "smartQrCodes/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleSmartQrCode(id);
    if (res.status === "success") return res.data.smart_qr_code;
    return rejectWithValue(res.message);
  }
);

export const createNewSmartQrCode = createAsyncThunk(
  "smartQrCodes/create",
  async (data, { rejectWithValue }) => {
    const res = await createSmartQrCode(data);
    if (res?.status === "success" || res?.success) {
      return res.data?.smart_qr_code || res.data || res.smart_qr_code || null;
    }
    return rejectWithValue(res?.message || "Failed to create smart QR code");
  }
);

export const updateExistingSmartQrCode = createAsyncThunk(
  "smartQrCodes/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateSmartQrCode(id, data);
    if (res?.status === "success" || res?.success) {
      return res.data?.smart_qr_code || res.data || res.smart_qr_code || null;
    }
    return rejectWithValue(res?.message || "Failed to update smart QR code");
  }
);

export const updateToggleSmartQrCode = createAsyncThunk("smartQrCodes/toggle", async ({ id, status }, { rejectWithValue }) => {
    const res = await toggleSmartQrCode(id, status);
    if (res.status === "success") return { id, status };
    return rejectWithValue(res.message);
});

export const deleteSmartQrCodeById = createAsyncThunk(
  "smartQrCodes/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteSmartQrCode(id);
    if (res?.status === "success" || res?.success) return id;
    return rejectWithValue(res?.message || "Failed to delete smart QR code");
  }
);

// Initial State

const initialState = {
  smartQrCodes: [],
  pagination: null,
  singleSmartQrCode: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const smartQrCodeSlice = createSlice({
  name: "smartQrCodes",
  initialState,
  reducers: {
    clearSmartQrCodeMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
    clearSingleSmartQrCode: (state) => {
      state.singleSmartQrCode = null;
    }
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllSmartQrCode.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllSmartQrCode.fulfilled, (state, action) => {        
        state.getLoading = false;
        state.smartQrCodes = action.payload?.smartQrCodes || [];
        state.pagination = action.payload?.pagination || null;
        state.requestStatus = true;
      })
      .addCase(fetchAllSmartQrCode.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleSmartQrCode.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleSmartQrCode.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleSmartQrCode = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleSmartQrCode.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewSmartQrCode.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewSmartQrCode.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload) {
          state.smartQrCodes.unshift(action.payload);
        }
        state.success = "Smart QR Code created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewSmartQrCode.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingSmartQrCode.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingSmartQrCode.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.smartQrCodes = state.smartQrCodes.map((qrCode) =>
          qrCode.id === action.payload?.id ? action.payload : qrCode
        );
        state.success = "Smart QR Code updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingSmartQrCode.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle availability
      .addCase(updateToggleSmartQrCode.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateToggleSmartQrCode.fulfilled, (state, action) => {
          state.updateLoading = false;          
          state.smartQrCodes = state.smartQrCodes.map((qrCode) =>
            qrCode.id === action.payload.id
              ? {
                  ...qrCode,
                  status: action.payload.status,
                }
              : qrCode
          );
          state.success = "Updated successfully!";
          state.requestStatus = true;
      })
      .addCase(updateToggleSmartQrCode.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })

      // Delete
      .addCase(deleteSmartQrCodeById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteSmartQrCodeById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.smartQrCodes = state.smartQrCodes.filter(
          (qrCode) => qrCode.id !== action.payload
        );
        state.success = "Smart QR Code deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteSmartQrCodeById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearSmartQrCodeMessages, clearSingleSmartQrCode } = smartQrCodeSlice.actions;
export default smartQrCodeSlice.reducer;
